import { Component, computed, inject, input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { Day, MD_DIALOG_REF, MDAlertService, MDDatePickerComponent, MDDialogModule, MDDialogRef, MDIconComponent } from '@melodic-ui/components';
import { CoreRescheduleService } from '../../../api/core/services/core-reschedule.service';
import {
	ICommitRescheduleRequest,
	IRescheduleOptionsResponse,
	IRescheduleSlot,
	RescheduleInitiator,
	RescheduleSlotGroupType
} from '../../../api/core/interfaces';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { LoaderComponent } from '../../loader/loader.component';
import { TzDatePipe, TzTimePipe } from '@lb/pipes/timezone-aware-date.pipe';
import { GlobalTimezoneService } from '../../../services/global-timezone.service';
import { take } from 'rxjs';

interface IDateGroup {
	dateLabel: string;
	dateKey: string;
	slots: IRescheduleSlot[];
}

@Component({
	selector: 'lb-learner-reschedule-lesson-dialog-v2',
	standalone: true,
	imports: [TzDatePipe, TzTimePipe, MDIconComponent, MDDialogModule, MDDatePickerComponent, LoaderComponent],
	templateUrl: './learner-reschedule-lesson-dialog-v2.component.html',
	styleUrl: './learner-reschedule-lesson-dialog-v2.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class LearnerRescheduleLessonDialogV2Component implements OnInit {
	private dialogRef: MDDialogRef = inject(MD_DIALOG_REF);
	private _rescheduleService = inject(CoreRescheduleService);
	private _alertService = inject(MDAlertService);
	private _globalTimezoneService = inject(GlobalTimezoneService);

	lesson = input<ILessonListItem>();

	loading = signal(true);
	rescheduling = signal(false);
	options = signal<IRescheduleOptionsResponse | null>(null);
	selectedSlot = signal<IRescheduleSlot | null>(null);
	useAfterLastSession = signal(false);
	selectedCalendarDate = signal<Date | null>(null);

	afterLastSessionSlot = computed(() => {
		const groups = this.options()?.groups ?? [];
		const group = groups.find((g) => g.type === RescheduleSlotGroupType.AfterLastSession);
		return group?.slots[0] ?? null;
	});

	slotsByDate = computed<IDateGroup[]>(() => {
		const groups = this.options()?.groups ?? [];
		const flexGroup = groups.find((g) => g.type === RescheduleSlotGroupType.FlexAvailability);
		if (!flexGroup?.slots.length) return [];

		const ianaTz = this._globalTimezoneService.selectedTimezone()?.timeZoneName ?? 'UTC';
		const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: ianaTz }); // YYYY-MM-DD in local TZ
		const labelFmt = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: ianaTz });

		const map = new Map<string, { label: string; slots: IRescheduleSlot[] }>();
		for (const slot of flexGroup.slots) {
			const date = new Date(slot.date);
			const dateKey = keyFmt.format(date);
			if (!map.has(dateKey)) map.set(dateKey, { label: labelFmt.format(date), slots: [] });
			map.get(dateKey)!.slots.push(slot);
		}

		return Array.from(map.entries()).map(([dateKey, { label, slots }]) => ({
			dateKey,
			dateLabel: label,
			slots
		}));
	});

	availableDateKeys = computed<Set<string>>(() => new Set(this.slotsByDate().map((g) => g.dateKey)));

	quickSlots = computed<IRescheduleSlot[]>(() => {
		const result: IRescheduleSlot[] = [];
		for (const group of this.slotsByDate()) {
			for (const slot of group.slots) {
				result.push(slot);
				if (result.length >= 3) return result;
			}
		}
		return result;
	});

	slotsForSelectedDate = computed<IRescheduleSlot[]>(() => {
		const selected = this.selectedCalendarDate();
		if (!selected) return [];
		const ianaTz = this._globalTimezoneService.selectedTimezone()?.timeZoneName ?? 'UTC';
		const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: ianaTz });
		// Use noon to prevent midnight-in-local-TZ flipping to wrong day in user's IANA TZ
		const noon = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);
		const key = keyFmt.format(noon);
		return this.slotsByDate().find((g) => g.dateKey === key)?.slots ?? [];
	});

	hasNoOptions = computed(() => {
		if (this.loading()) return false;
		return !this.afterLastSessionSlot() && this.slotsByDate().length === 0;
	});

	canSubmit = computed(() => !this.rescheduling() && (this.useAfterLastSession() || this.selectedSlot() !== null));

	// Format using LOCAL date components — no IANA conversion — since selectedCalendarDate
	// is a midnight-local Date that mirrors exactly what the calendar widget displays.
	selectedCalendarDateLabel = computed<string | null>(() => {
		const d = this.selectedCalendarDate();
		if (!d) return null;
		return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(d);
	});

	ngOnInit(): void {
		const lesson = this.lesson();
		if (!lesson) return;

		this._rescheduleService
			.getOptions(lesson.lessonID, RescheduleInitiator.Learner)
			.pipe(take(1))
			.subscribe({
				next: (response) => {
					this.options.set(response);
					this.loading.set(false);
					const firstGroup = this.slotsByDate()[0];
					if (firstGroup) {
						const [year, month, day] = firstGroup.dateKey.split('-').map(Number);
						// Midnight matches the date-picker's internal timestamp representation
						this.selectedCalendarDate.set(new Date(year, month - 1, day, 0, 0, 0));
					}
				},
				error: () => {
					this.loading.set(false);
					this._alertService.addAlert({
						type: 'error',
						title: 'Unable to Load Options',
						message: 'Could not load available reschedule times. Please try again.',
						duration: 4000
					});
				}
			});
	}

	selectSlot(slot: IRescheduleSlot): void {
		this.useAfterLastSession.set(false);
		this.selectedSlot.set(slot);
	}

	selectQuickSlot(slot: IRescheduleSlot): void {
		this.useAfterLastSession.set(false);
		this.selectedSlot.set(slot);
		const ianaTz = this._globalTimezoneService.selectedTimezone()?.timeZoneName ?? 'UTC';
		const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: ianaTz });
		const dateKey = keyFmt.format(new Date(slot.date));
		const [year, month, day] = dateKey.split('-').map(Number);
		// Midnight matches the date-picker's internal timestamp representation
		this.selectedCalendarDate.set(new Date(year, month - 1, day, 0, 0, 0));
	}

	updateCalendarDate(date: Date): void {
		this.useAfterLastSession.set(false);
		// Store at midnight to match the date-picker's internal timestamp — noon trick is
		// applied in slotsForSelectedDate and disableCalendarDay for IANA key comparisons.
		this.selectedCalendarDate.set(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
		this.selectedSlot.set(null);
	}

	disableCalendarDay(): (day: Day) => boolean {
		const available = this.availableDateKeys();
		const ianaTz = this._globalTimezoneService.selectedTimezone()?.timeZoneName ?? 'UTC';
		const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: ianaTz });
		return (day: Day) => {
			// The calendar creates days at midnight LOCAL time. Formatting midnight in a different
			// IANA TZ can shift to the prior/next day. Using noon ensures a ±12h offset never flips the date.
			const noon = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate(), 12, 0, 0);
			return !available.has(keyFmt.format(noon));
		};
	}

	selectAfterLastSession(): void {
		this.selectedSlot.set(null);
		this.useAfterLastSession.set(true);
	}

	isSlotSelected(slot: IRescheduleSlot): boolean {
		return this.selectedSlot()?.date === slot.date;
	}

	reschedule(): void {
		const lesson = this.lesson();
		if (!lesson || !this.canSubmit()) return;

		this.rescheduling.set(true);

		const request: ICommitRescheduleRequest = {
			lessonID: lesson.lessonID,
			proposedDate: this.useAfterLastSession() ? null : this.selectedSlot()?.date ?? null,
			propose: false,
			initiatedBy: RescheduleInitiator.Learner
		};

		this._rescheduleService
			.commit(lesson.lessonID, request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.rescheduling.set(false);
					this.dialogRef.close(true);
				},
				error: (error) => {
					this.rescheduling.set(false);
					this._alertService.addAlert({
						type: 'error',
						title: 'Reschedule Failed',
						message: error?.error?.message || 'Something went wrong rescheduling the lesson.',
						duration: 4000
					});
				}
			});
	}

	close(): void {
		this.dialogRef.close();
	}
}
